import React from 'react';
import classNameBind from 'classnames/bind';

import { useTranslation } from '../Translator';
import Button from '../Button';
import ButtonGroup from '../ButtonGroup';
import Popover from '../Popover';
import Caption from '../Caption';
import { Select } from '../Select';
import { PageForm } from './components/PageForm';
import styles from './Pagination.module.css';

const bindClasses = classNameBind.bind(styles);

const Pagination = ({
  total,
  limit,
  appearance = 'default',
  onLimitChange = () => {},
  startIndex,
  endIndex,
  showTotal = true,
  limitOptions = [10, 25, 50, 100],
  forcePrevDisabled = false,
  forceNextDisabled = false,
  dataId = 'Pagination',
  showLimitLabel = true,
  onPageChange,
}) => {
  const { t } = useTranslation('components');
  const getPageNumber = () => Math.floor(startIndex / limit) + 1;
  const getLastPageNumber = () => {
    const lastPageNumber = Math.ceil(total / limit);

    return lastPageNumber === 0 ? 1 : lastPageNumber;
  };
  const getPageLabel = () => `${t('page')} ${t('number', { number: getPageNumber() })}  ${t('outOfLabel')} ${getLastPageNumber()}`;
  const getLimitOptions = () => (
    [{
      id: 0,
      name: '',
      items: Array.from(
        limitOptions, count => ({
          name: count.toString(),
          id: count.toString(),
          value: count.toString(),
        }),
      ),
    }]
  );
  const goToPage = (page) => {
    const newEndIndex = page * limit;
    const newStartIndex = newEndIndex - limit;

    onPageChange(newStartIndex, newEndIndex);
  };
  const onNext = () => {
    goToPage(getPageNumber() + 1);
  };
  const onPrev = () => {
    goToPage(getPageNumber() - 1);
  };

  const pageNumber = String(getPageNumber());
  const prevDisabled = forcePrevDisabled || startIndex < limit;
  const unknownTotal = total === -1;
  const nextDisabled = forceNextDisabled || (unknownTotal ? false : (total <= endIndex));

  return (
    <div
      className={bindClasses('wrapper', appearance)}
      data-ppuc-id={dataId}
    >
      {showLimitLabel && (
        <div className={bindClasses('number-label')}>
          <Caption>{t('itemsPerPage')}</Caption>
        </div>
      )}
      <Select
        name="items_per_page"
        options={getLimitOptions()}
        onChange={event => onLimitChange(Number(event.target.value))}
        value={String(limit)}
        inline
        disabled={limitOptions.length < 2}
        dataId={`${dataId}.itemsPerPage`}
        type={appearance === 'compact' ? 'subduedCompactSmall' : ''}
      />
      <div className={bindClasses('wide')}>
        {showTotal && !unknownTotal && appearance !== 'compact' && (
          <Caption subdued>
            {`${t('number', { number: Number(total) })} ${t('items')}`}
          </Caption>
        )}
      </div>
      {!unknownTotal && (
        <div className={bindClasses('page-count-label')}>
          <Caption subdued noSelect>
            {getPageLabel()}
          </Caption>
        </div>
      )}
      <ButtonGroup segmented>
        <Button icon="arrow-left" disabled={prevDisabled} onClick={onPrev} size={appearance === 'compact' ? 'small' : 'large'} />
        <Popover
          position="top-start"
          minimalWidth
          disabled={(prevDisabled && nextDisabled) || unknownTotal}
          activatorElement={(
            <Button
              text={pageNumber}
              disabled={(prevDisabled && nextDisabled) || unknownTotal}
              minimalWidth
              size={appearance === 'compact' ? 'small' : 'large'}
            />
          )}
          speechBubble
          openProps={{ appearance: 'primary' }}
          closedProps={{ appearance: 'default' }}
          childToggle={{
            onChange: () => true,
          }}
          dataId={`${dataId}.popover`}
        >
          <PageForm
            label={t('jumpToPage')}
            placeholder={pageNumber}
            onChange={goToPage}
            lastPage={getLastPageNumber()}
          />
        </Popover>
        <Button icon="arrow-right" disabled={nextDisabled} onClick={onNext} size={appearance === 'compact' ? 'small' : 'large'} />
      </ButtonGroup>
    </div>
  );
};

export { Pagination };
